/**
 * Hook: useContentActions
 *
 * Manages state for content actions bottom sheet.
 * Matches Flutter implementation: lib/presentation/bottom_sheet/content_actions_bottom_sheet.dart
 */

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import { useListenToSecureStorageUpdates } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { SavedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { generateUserContentShareDataUseCase } from '@/domain/usecases/share/generateUserContentShareDataUseCase';
import type { ShareContent } from '@/domain/usecases/share/shareContent';
import { saveContentToWatchLaterUseCase } from '@/domain/usecases/media/saveContentToWatchLaterUseCase';
import { hideContentUseCase } from '@/domain/usecases/media/hideContentUseCase';
import { deleteContentUseCase } from '@/domain/usecases/media/deleteContentUseCase';
import { forcePublishContentUseCase } from '@/domain/usecases/media/forcePublishContentUseCase';
import { linkContentUseCase } from '@/domain/usecases/media/linkContentUseCase';
import { unlinkContentUseCase } from '@/domain/usecases/media/unlinkContentUseCase';
import { getUserUseCase } from '@/domain/usecases/user/getUserUseCase';
import { publishScheduledContent } from '@/presentation/feature/content/publishScheduledContent';
import { storeCurrentUserToLocalStorageUseCase } from '@/domain/usecases/user/storeCurrentUserToLocalStorageUseCase';
import { setUser } from '@/presentation/store/slices/auth/authSlice';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseContentActionsProps {
  media: UserMediaEntity;
}

export interface UseContentActionsResult {
  isCurrentUser: boolean;
  contentIsSaved: boolean;
  generateShareData: () => Promise<ShareContent>;
  saveToWatchLater: () => Promise<boolean>;
  hideContent: () => Promise<boolean>;
  deleteContent: () => Promise<boolean>;
  publishDraft: () => Promise<boolean>;
  publishScheduled: () => Promise<boolean>;
  linkContent: (destination: UserMediaEntity) => Promise<boolean>;
  unlinkContent: () => Promise<boolean>;
}

export function useContentActions({ media }: UseContentActionsProps): UseContentActionsResult {
  const dispatch = useDispatch();

  // Get current authenticated user
  const currentFirebaseUser = getAuthFirebaseUserUseCase.execute();
  const currentUserId = currentFirebaseUser?.uid;

  // Determine if viewing own content
  const isCurrentUser = useMemo(() => {
    if (!currentUserId || !media.firebaseId) return false;
    return currentUserId === media.firebaseId;
  }, [currentUserId, media.firebaseId]);

  // Reactive storage check for saved content (Web equivalent of Flutter's SavedContent.keyName + relativePath; Web key convention adds a '_' suffix: 'SavedContent_' vs Flutter's 'SavedContent')
  const savedStorageKey = SECURE_STORAGE_KEYS.SAVED_CONTENT + media.relativePath;
  const { data: savedContentData } = useListenToSecureStorageUpdates<SavedContentStorage>(savedStorageKey);
  const contentIsSaved = savedContentData !== null;

  const generateShareData = useCallback(
    () => generateUserContentShareDataUseCase.execute(media),
    [media],
  );

  const saveToWatchLater = useCallback(
    () => saveContentToWatchLaterUseCase.execute(media),
    [media],
  );

  const refreshCurrentUser = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const updatedUser = await getUserUseCase.execute(currentUserId);
      if (updatedUser) {
        await storeCurrentUserToLocalStorageUseCase.execute(updatedUser);
        dispatch(setUser(updatedUser));
      }
    } catch (err) {
      appLogger.error('Failed to refresh current user', 'useContentActions.refreshCurrentUser', { error: err as Error });
    }
  }, [currentUserId, dispatch]);

  const hideContent = useCallback(async () => {
    const success = await hideContentUseCase.execute(media);
    if (!success) return false;

    eventBus.emit(APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED, { mediaId: media.id });
    await refreshCurrentUser();
    return true;
  }, [media, refreshCurrentUser]);

  const deleteContent = useCallback(async () => {
    const success = await deleteContentUseCase.execute(media);
    if (!success) return false;

    eventBus.emit(APP_EVENT_KEYS.CONTENT_LIST_ITEM_REMOVED, { mediaId: media.id });
    await refreshCurrentUser();
    return true;
  }, [media, refreshCurrentUser]);

  const publishDraft = useCallback(async () => {
    const success = await forcePublishContentUseCase.execute(media.id);
    if (success) {
      eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, {
        mediaId: media.id,
        patch: { isDraft: false },
      });
    }
    return success;
  }, [media.id]);

  // Publish+emit pair is owned by `publishScheduledContent` so the inbox
  // notification-tap handler can run the identical flow without a hook.
  const publishScheduled = useCallback(() => publishScheduledContent(media.id), [media.id]);

  const linkContent = useCallback(
    async (destination: UserMediaEntity) => {
      const success = await linkContentUseCase.execute(media, destination);
      if (success) {
        // Both sides of a link write back-to-back in Firestore — emit one event
        // per side so any consumer holding either media can refresh its label.
        eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, {
          mediaId: media.id,
          patch: { contentLink: destination.relativePath },
        });
        eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, {
          mediaId: destination.id,
          patch: { contentLink: media.relativePath },
        });
      }
      return success;
    },
    [media],
  );

  const unlinkContent = useCallback(async () => {
    const peerMediaId = await unlinkContentUseCase.execute(media);
    if (peerMediaId == null) return false;

    eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, {
      mediaId: media.id,
      patch: { contentLink: null },
    });
    eventBus.emit(APP_EVENT_KEYS.CONTENT_ITEM_UPDATED, {
      mediaId: peerMediaId,
      patch: { contentLink: null },
    });
    return true;
  }, [media]);

  return {
    isCurrentUser,
    contentIsSaved,
    generateShareData,
    saveToWatchLater,
    hideContent,
    deleteContent,
    publishDraft,
    publishScheduled,
    linkContent,
    unlinkContent,
  };
}
